// src/components/OTPVerification.tsx
import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'

interface OTPVerificationProps {
  email: string
  loading: boolean
  onSubmit: (otp: string) => Promise<void>
  onResend: () => Promise<void>
  onBack: () => void
}

export default function OTPVerification({
  email,
  loading,
  onSubmit,
  onResend,
  onBack
}: OTPVerificationProps) {
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [resendDisabled, setResendDisabled] = useState(false)
  const [countdown, setCountdown] = useState(30)

  // Countdown timer for resend
  useEffect(() => {
    let timer: NodeJS.Timeout
    if (resendDisabled && countdown > 0) {
      timer = setTimeout(() => setCountdown(prev => prev - 1), 1000)
    } else if (countdown === 0 && resendDisabled) {
      setResendDisabled(false)
      setCountdown(30)
    }
    return () => clearTimeout(timer)
  }, [resendDisabled, countdown])

  const handleOtpChange = (index: number, value: string) => {
    // Allow only numbers
    if (!/^\d*$/.test(value)) return
    
    const newOtp = [...otp]
    newOtp[index] = value.slice(0, 1)
    setOtp(newOtp)

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`)
      nextInput?.focus()
    }
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`)
      prevInput?.focus()
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text')
    const pastedNumbers = pastedData.replace(/\D/g, '').slice(0, 6)
    
    if (pastedNumbers) {
      const newOtp = [...otp]
      pastedNumbers.split('').forEach((char, index) => {
        if (index < 6) newOtp[index] = char
      })
      setOtp(newOtp)
      
      const nextEmptyIndex = newOtp.findIndex(digit => !digit)
      if (nextEmptyIndex !== -1) {
        document.getElementById(`otp-${nextEmptyIndex}`)?.focus()
      } else {
        document.getElementById('otp-5')?.focus()
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const otpString = otp.join('')
    if (otpString.length !== 6) {
      toast.error('Please enter complete 6-digit code')
      return
    }

    try {
      await onSubmit(otpString)
    } catch (error) {
      console.error('❌ OTP submission error:', error)
    }
  }

  const handleResend = async () => {
    try {
      setResendDisabled(true)
      console.log('📧 Resending OTP...')
      await onResend()
      toast.success('Check your email for a new verification code')
    } catch (error) {
      console.error('❌ Resend OTP error:', error)
      toast.error('Failed to resend code. Please try again.')
      setResendDisabled(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg animate-fadeIn">
        <div className="text-center">
          <div className="text-5xl mb-4">📧</div>
          <h2 className="text-3xl font-bold text-gray-900">Verify Your Email</h2>
          <p className="text-gray-600 mt-3">
            We've sent a verification code to<br />
            <span className="font-semibold text-indigo-600">{email}</span>
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Enter the 6-digit code below
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          {/* OTP Input Fields */}
          <div className="flex justify-center gap-2">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                pattern="\d*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                onPaste={index === 0 ? handleOtpPaste : undefined}
                className="w-12 h-12 text-center text-xl font-semibold border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                autoFocus={index === 0}
                disabled={loading}
              />
            ))}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || otp.some(d => !d)}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Verifying...
              </span>
            ) : 'Verify & Continue'}
          </button>

          {/* Resend & Back Buttons */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleResend}
              disabled={resendDisabled || loading}
              className="text-indigo-600 hover:text-indigo-700 text-sm font-medium disabled:text-gray-400"
            >
              {resendDisabled ? `Resend code in ${countdown}s` : 'Resend code'}
            </button>

            <button
              type="button"
              onClick={onBack}
              className="text-gray-600 hover:text-gray-800 text-sm font-medium"
              disabled={loading}
            >
              ← Back
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}